import { useEffect, useMemo, useState } from "react";
import { Sparkle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Encouraging facts for business owners — sourced from peer-reviewed and industry research.
 * Rendered as a short "flat" ticker card that sits under the Qualification section.
 * Each fact has a tiny superscript "#" that opens a citation popup: the spotlighted fact
 * (with its source link) at the top, and every other fact listed below with its own link.
 */
const FACTS = [
  { id:  1, text: "35% MORE PROFIT",                                                url: "https://academic.oup.com/jeea/article/19/5/2622/6134759" },
  { id:  2, text: "17% MORE SALES",                                                 url: "https://academic.oup.com/jeea/article/19/5/2622/6134759" },
  { id:  3, text: "17% MORE PRODUCTIVE",                                            url: "https://academic.oup.com/qje/article-abstract/128/1/1/1838606" },
  { id:  4, text: "14–18% MORE GOOD MANAGEMENT HABITS",                             url: "https://academic.oup.com/restud/article-abstract/89/1/346/6149318" },
  { id:  5, text: "44% MORE EMPLOYEES",                                             url: "https://openknowledge.worldbank.org/bitstreams/141c01fd-f443-5b1e-ae76-30faf59bbe87/download" },
  { id:  6, text: "153% FASTER SALES GROWTH",                                       url: "https://americassbdc.org/wp-content/uploads/2025/02/Americas-SBDC-2025-impact-bifold-draft3-WEB.pdf" },
  { id:  7, text: "28% USE A BUSINESS MENTOR",                                      url: "https://www.sage.com/en-gb/blog/wp-content/uploads/sites/10/2017/08/business-mentoring.pdf" },
  { id:  8, text: "72% DO NOT USE ONE",                                             url: "https://www.sage.com/en-gb/blog/wp-content/uploads/sites/10/2017/08/business-mentoring.pdf" },
  { id:  9, text: "88% SAY THEIR MENTOR IS INVALUABLE",                             url: "https://www.theupsstore.com/about/pressroom/small-business-mentoring-month-2014" },
  { id: 10, text: "70% OF MENTORED BUSINESSES LAST 5+ YEARS",                       url: "https://www.sba.gov/blog/mentoring-missing-link-small-business-growth-survival" },
  { id: 11, text: "ABOUT 40% MORE LIKELY TO LAST 5 YEARS",                          url: "https://www.sba.gov/blog/mentoring-missing-link-small-business-growth-survival" },
  { id: 12, text: "49% USE PHONE COACHING",                                         url: "https://fr.scribd.com/document/812519479/2023-ICF-Global-Coaching-Study-United-States" },
  { id: 13, text: "HIGH-GROWTH FOUNDERS AVERAGE AGE 45",                            url: "https://www.nber.org/digest/jul18/when-it-comes-entrepreneurs-youth-isnt-everything" },
  { id: 14, text: "50-YEAR-OLD FOUNDERS ARE 80% MORE LIKELY TO BUILD A HIGH-GROWTH COMPANY", url: "https://www.nber.org/system/files/working_papers/w24489/w24489.pdf" },
  { id: 15, text: "MOST MILLIONAIRES BECOME MILLIONAIRES AROUND AGE 50",            url: "https://www.ramseysolutions.com/retirement/how-many-millionaires-in-us" },
];

const HOLD_MS = 4200;
const FADE_MS = 500;

export default function SupportiveFacts() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [openId, setOpenId] = useState(null);   // which fact is spotlighted in the popup

  // Cross-fade rotation; pauses whenever the popup is open so a curious reader
  // isn't reading a spotlight for a fact that's already scrolled past on the ticker.
  useEffect(() => {
    if (openId !== null) return;
    const tick = setTimeout(() => {
      setVisible(false);
      const fade = setTimeout(() => {
        setIdx((i) => (i + 1) % FACTS.length);
        setVisible(true);
      }, FADE_MS);
      return () => clearTimeout(fade);
    }, HOLD_MS);
    return () => clearTimeout(tick);
  }, [idx, openId]);

  const spotlight = useMemo(
    () => FACTS.find((f) => f.id === openId) || null,
    [openId]
  );
  const rest = useMemo(
    () => (spotlight ? FACTS.filter((f) => f.id !== spotlight.id) : []),
    [spotlight]
  );

  const current = FACTS[idx];

  return (
    <>
      <div
        data-testid="supportive-facts-card"
        className="rounded-2xl bg-cvc-navy text-cvc-cream border border-cvc-navy shadow-sm px-5 md:px-7 py-4 md:py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6"
      >
        <div className="flex items-center gap-3 shrink-0">
          <Sparkle size={22} weight="fill" className="text-amber-400" />
          <div className="leading-tight">
            <p className="text-[10px] tracking-[0.3em] uppercase font-body font-semibold text-amber-400/90">
              Supporting your dreams
            </p>
            <p className="font-heading text-base md:text-lg">Owners who get help do measurably better.</p>
          </div>
        </div>

        <div className="hidden md:block h-10 w-px bg-cvc-cream/20 shrink-0" />

        <div className="flex-1 min-h-[38px] flex items-center" aria-live="polite">
          <p
            data-testid="supportive-facts-item"
            className="font-body text-sm md:text-base tracking-wide transition-opacity"
            style={{ opacity: visible ? 1 : 0, transitionDuration: `${FADE_MS}ms` }}
          >
            <span className="align-middle">{current.text}</span>
            <button
              type="button"
              onClick={() => setOpenId(current.id)}
              aria-label={`View citation for: ${current.text}`}
              data-testid={`supportive-fact-cite-${current.id}`}
              className="ml-1.5 align-super text-[10px] font-body text-amber-400 hover:text-white underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 rounded"
            >
              #
            </button>
          </p>
        </div>
      </div>

      <Dialog open={openId !== null} onOpenChange={(open) => !open && setOpenId(null)}>
        <DialogContent
          data-testid="supportive-facts-dialog"
          className="max-w-2xl bg-cvc-cream text-cvc-ink border-cvc-border"
        >
          <DialogTitle className="font-heading text-2xl text-cvc-navy">
            Where these numbers come from
          </DialogTitle>
          <DialogDescription className="font-body text-sm text-cvc-ink-soft">
            Every stat is drawn from peer-reviewed research or reputable industry data. The one you clicked is spotlighted first; the rest are listed underneath.
          </DialogDescription>

          {spotlight && (
            <div
              data-testid="supportive-facts-spotlight"
              className="mt-2 rounded-xl border border-cvc-navy/25 bg-white px-5 py-4"
            >
              <p className="text-[10px] tracking-[0.28em] uppercase font-body font-semibold text-cvc-red">
                You clicked
              </p>
              <p className="font-heading text-lg md:text-xl text-cvc-navy mt-1">
                <span className="text-amber-500 mr-1.5">◆</span>{spotlight.text}
              </p>
              <a
                href={spotlight.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`supportive-facts-spotlight-link-${spotlight.id}`}
                className="inline-block mt-2 text-sm text-cvc-red hover:text-cvc-red-deep underline underline-offset-4 break-all"
              >
                {spotlight.url}
              </a>
            </div>
          )}

          <div className="mt-1 max-h-[45vh] overflow-y-auto pr-1">
            <p className="text-[10px] tracking-[0.28em] uppercase font-body font-semibold text-cvc-ink-soft mb-2">
              The rest of the facts
            </p>
            <ul className="space-y-3">
              {rest.map((f) => (
                <li key={f.id} data-testid={`supportive-facts-rest-${f.id}`} className="border-b border-cvc-border pb-3 last:border-b-0">
                  <p className="font-body text-sm md:text-[15px] text-cvc-ink">
                    <span className="text-amber-500 mr-1.5">◆</span>{f.text}
                  </p>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-1 text-xs text-cvc-red hover:text-cvc-red-deep underline underline-offset-2 break-all"
                  >
                    {f.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
